import I18n from 'components/I18n'
import { OverlayLoader } from 'components/Loaders'
import { actions, selectors } from 'data'
import { DepositMethodType, WithdrawalMethodType } from 'data/Analytics/types'
import { Bank } from 'data/Payments/BankTransfer/types'
import { BeneficiaryType, StateType } from 'data/types'
import messages from 'lang'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import TargetOptions from 'scenes/Cashflow/BeneficiarySelect/TargetOptions'
import {
  Header,
  ModalCard,
  ModalTitle,
  StyledDarkTabMenuItem,
  StyledRoundedTabMenu,
  StyledSeparator
} from 'scenes/Cashflow/styles'

import DepositCryptoAddress from '../Deposit/Crypto/DepositCryptoAddress'
import DepositFormModal from '../Deposit/DepositFormModal'
import WireInstructionsModal from '../WireInstructionsModal'
import WithdrawCryptoFormModal from '../Withdrawal/Crypto/WithdrawCryptoFormModal'
import WithdrawFormModal from '../Withdrawal/Fiat/WithdrawFormModal'
import StableOptions from './StableOptions'

type Props = {
  currency: string
  flow: 'DEPOSIT' | 'WITHDRAW'
}
const FiatModal = ({ currency, flow }: Props) => {
  const [tab, setTab] = useState<'BANK' | 'STABLECOINS'>('BANK')
  const [bank, setBank] = useState<Bank | null>(null)
  const [beneficiary, setBeneficiary] = useState<BeneficiaryType | null>(null)
  const [stablecoin, setStablecoin] = useState<string | null>(null)
  const [wireTransfer, setWireTransfer] = useState(false)
  const dispatch = useDispatch()
  const balances = useSelector(selectors.account.getBalances)
  const canBankTransfer = useSelector((state: StateType) =>
    selectors.payments.eligible.canBankTransfer(state)(currency)
  )
  const beneficiariesLoading = useSelector(selectors.payments.getBeneficiariesLoading)
  const allBeneficiaries = useSelector(selectors.payments.getBeneficiaries)
  const beneficiaries = allBeneficiaries.filter(
    (ben) => ben.currency === currency && ben.state === 'ACTIVE'
  )
  const isFetchingBanks = useSelector(selectors.payments.bankTransfer.isFetchingBanks)
  const allBanks = useSelector(selectors.payments.bankTransfer.getActiveBanks)
  const banks = allBanks
    .filter((bank) => bank.currency === currency)
    .sort((current, next) => (current?.addedAt > next?.addedAt ? 1 : -1))
  const instrumentCurrencies = useSelector(selectors.instrument.getRawCurrencies)
  const eligibility = useSelector(selectors.eligibility.getEligibility)
  const isWithdraw = flow === 'WITHDRAW'
  const fiatEligibility = isWithdraw ? eligibility.withdrawFiat : eligibility.depositFiat
  const cryptoEligibility = isWithdraw ? eligibility.withdrawCrypto : eligibility.depositCrypto

  const trackChangeWithdrawMethod = (method) =>
    dispatch(
      actions.analytics.trackEvent({
        key: 'Withdrawal Method Selected',
        properties: {
          currency,
          to_account_type: null,
          withdrawal_method: method as WithdrawalMethodType
        }
      })
    )

  const trackChangeDepositMethod = (method, stablecoin?: string) =>
    dispatch(
      actions.analytics.trackEvent({
        key: 'Exchange Deposit Fiat Method Selected',
        properties: {
          currency,
          deposit_method: method as DepositMethodType,
          stablecoin
        }
      })
    )

  const trackChangeMethod = (method, stablecoin?: string) => {
    return isWithdraw
      ? trackChangeWithdrawMethod(method)
      : trackChangeDepositMethod(method, stablecoin)
  }

  const handleSelectBeneficiary = (beneficiary) => {
    trackChangeMethod('BANK_ACCOUNT')
    setBank(null)
    setBeneficiary(beneficiary)
    setStablecoin(null)
    setWireTransfer(false)
  }

  const handleSelectBank = (bank) => {
    trackChangeMethod('BANK_ACCOUNT')
    setBank(bank)
    setBeneficiary(null)
    setStablecoin(null)
    setWireTransfer(false)
  }

  const handleWireTransfer = () => {
    trackChangeMethod('BANK_TRANSFER')
    setBank(null)
    setBeneficiary(null)
    setStablecoin(null)
    setWireTransfer(true)
  }

  const handleStablecoin = (currency) => {
    trackChangeMethod('STABLECOIN', currency)
    setBank(null)
    setBeneficiary(null)
    setStablecoin(currency)
    setWireTransfer(false)
  }

  const handleBack = () => {
    setBank(null)
    setBeneficiary(null)
    setStablecoin(null)
    setWireTransfer(false)
  }

  useEffect(() => {
    dispatch(actions.payments.elegible.fetchElegiblePMs(currency))
    dispatch(actions.payments.fetchBanks())
    dispatch(actions.payments.fetchBeneficiaries())
  }, [currency])

  const withdrawLoading = !balances || !Object.keys(balances).length || beneficiariesLoading
  const isLoading = (isWithdraw && withdrawLoading) || isFetchingBanks

  const instrumentCurrency = instrumentCurrencies.find((c) => c.key === currency)
  if (!instrumentCurrency) return null

  const targetCurrencies = isWithdraw
    ? instrumentCurrency.withdrawnAs
    : instrumentCurrency.toppedUpFrom
  const stablecoinOptions = targetCurrencies.filter((symbol) => symbol !== instrumentCurrency.key)

  if (wireTransfer) {
    return <WireInstructionsModal currency={currency} flow={flow} onBack={handleBack} />
  }

  if (stablecoin) {
    if (isWithdraw) {
      return <WithdrawCryptoFormModal currency={stablecoin} />
    }

    return <DepositCryptoAddress currency={stablecoin} />
  }

  if (bank) {
    if (isWithdraw) {
      return (
        <WithdrawFormModal currency={currency} bank={bank} onSelectMethod={() => setBank(null)} />
      )
    }
    return <DepositFormModal currency={currency} bank={bank} onSelectMethod={() => setBank(null)} />
  }

  if (beneficiary) {
    return (
      <WithdrawFormModal
        currency={currency}
        beneficiary={beneficiary}
        onSelectMethod={() => setBeneficiary(null)}
      />
    )
  }

  return (
    <ModalCard>
      {isLoading && <OverlayLoader />}
      <Header>
        <ModalTitle>
          <I18n
            data={isWithdraw ? messages.withdrawl_currency : messages.deposit_currency}
            currency={currency}
          />
        </ModalTitle>
      </Header>
      {stablecoinOptions.length > 0 && (
        <StyledRoundedTabMenu>
          <StyledDarkTabMenuItem onClick={() => setTab('BANK')} selected={tab === 'BANK'}>
            <I18n data={messages.via_bank} />
          </StyledDarkTabMenuItem>
          <StyledDarkTabMenuItem
            onClick={() => setTab('STABLECOINS')}
            selected={tab === 'STABLECOINS'}
          >
            <I18n data={messages.via_stablecoins} />
          </StyledDarkTabMenuItem>
        </StyledRoundedTabMenu>
      )}
      <StyledSeparator />
      {tab === 'BANK' && (
        <TargetOptions
          banks={banks}
          beneficiaries={isWithdraw ? beneficiaries : []}
          canBankTransfer={canBankTransfer}
          currency={currency}
          eligibility={fiatEligibility}
          isWithdraw={isWithdraw}
          onBack={handleBack}
          onSelectBank={handleSelectBank}
          onSelectBeneficiary={handleSelectBeneficiary}
          onWireTransfer={handleWireTransfer}
        />
      )}
      {tab === 'STABLECOINS' && stablecoinOptions.length > 0 && (
        <StableOptions
          eligibility={cryptoEligibility}
          isWithdraw={isWithdraw}
          onItemClick={handleStablecoin}
          options={stablecoinOptions}
        />
      )}
    </ModalCard>
  )
}

export default FiatModal
